import { getAuthHeaders } from '../services/auth';

export interface SseEvent<T = Record<string, unknown>> {
  seq?: number;
  type: string;
  data: T;
  ts?: string;
}

/** 通用 SSE 帧（如对话流的 TOKEN/DONE 帧，无 seq 游标语义）。 */
export interface SseFrame {
  type: string;
  [key: string]: unknown;
}

/** 游标拉取（GET /jobs/{id}/events/history）的响应结构。 */
export interface JobEventsPage {
  job_id: string;
  events: SseEvent[];
  latest_seq: number;
  has_more: boolean;
}

export interface SseReadOptions {
  /** 起始游标：只接收 seq 大于该值的事件（断线续传） */
  after?: number;
  /** 事件流意外中断后的自动重连次数（默认 3；重连自动携带最新游标） */
  maxRetries?: number;
  /** 重连间隔毫秒（默认 800） */
  retryDelayMs?: number;
}

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/** 给 URL 追加/更新 after 查询参数。 */
function withAfter(url: string, after: number): string {
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}after=${after}`;
}

/**
 * 读取后端 SSE 事件流（GET + Authorization 头）。
 *
 * EventSource 无法设置请求头，因此用 fetch + ReadableStream 手动解析
 * `data: {...}\n\n` 帧。仅支持 data 字段（后端 jobs 事件流只有 data 行）。
 *
 * P11 游标重连：流在未收到 done 事件前意外中断时，按已收到的最大 seq
 * 作为游标自动重连（?after=<seq>），服务端补发 missed events，不重不漏；
 * 任务 404（不存在）或主动 abort 不重连。
 */
export async function readSseEvents(
  url: string,
  onEvent: (event: SseEvent) => void,
  signal?: AbortSignal,
  options?: SseReadOptions
): Promise<void> {
  const maxRetries = options?.maxRetries ?? 3;
  const retryDelayMs = options?.retryDelayMs ?? 800;
  let lastSeq = options?.after ?? 0;
  let sawDone = false;

  const handleEvent = (event: SseEvent) => {
    if (typeof event.seq === 'number' && event.seq > lastSeq) {
      lastSeq = event.seq;
    }
    if (event.type === 'done') sawDone = true;
    onEvent(event);
  };

  for (let attempt = 0; ; attempt++) {
    const res = await fetch(lastSeq > 0 ? withAfter(url, lastSeq) : url, {
      headers: getAuthHeaders(),
      signal,
    });
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      throw new Error(`订阅任务事件失败 (HTTP ${res.status})${text ? `: ${text}` : ''}`);
    }
    if (!res.body) {
      throw new Error('订阅任务事件失败：响应没有事件流');
    }

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    const dispatch = (chunk: string) => {
      const dataLines = chunk
        .split('\n')
        .filter((line) => line.startsWith('data:'))
        .map((line) => line.slice(5).replace(/^ /, ''));
      if (dataLines.length === 0) return;
      try {
        handleEvent(JSON.parse(dataLines.join('\n')) as SseEvent);
      } catch {
        // 忽略无法解析的事件帧，不中断流
      }
    };

    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const frames = buffer.split('\n\n');
      buffer = frames.pop() || '';
      frames.forEach(dispatch);
    }
    buffer += decoder.decode();
    if (buffer.trim()) dispatch(buffer);

    // 终态事件或主动中断：正常结束，不重连
    if (sawDone || signal?.aborted) return;
    // 流意外中断（未收到 done）：按游标重连补发；重试耗尽后抛出由调用方兜底
    if (attempt >= maxRetries) {
      throw new Error('任务事件流中断，自动重连失败（请刷新页面重试）');
    }
    await sleep(retryDelayMs);
  }
}

/** 从响应流逐帧解析 `data: {...}\n\n` 并回调（postSseStream / readSseFrames 共用）。 */
async function consumeSseFrames(body: ReadableStream<Uint8Array>, onFrame: (frame: SseFrame) => void): Promise<void> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  const dispatch = (chunk: string) => {
    const dataLines = chunk
      .split('\n')
      .filter((line) => line.startsWith('data:'))
      .map((line) => line.slice(5).replace(/^ /, ''));
    if (dataLines.length === 0) return;
    try {
      onFrame(JSON.parse(dataLines.join('\n')) as SseFrame);
    } catch {
      // 忽略无法解析的事件帧，不中断流
    }
  };

  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const frames = buffer.split('\n\n');
    buffer = frames.pop() || '';
    frames.forEach(dispatch);
  }
  buffer += decoder.decode();
  if (buffer.trim()) dispatch(buffer);
}

/** 读取 HTTP 错误体（detail 优先），拼装为 Error 抛出。 */
async function throwHttpError(res: Response, fallback: string): Promise<never> {
  const text = await res.text().catch(() => '');
  let detail = text;
  try {
    const parsed = JSON.parse(text) as { detail?: unknown };
    if (typeof parsed.detail === 'string' && parsed.detail) detail = parsed.detail;
  } catch {
    // 非 JSON 错误体，保留原文
  }
  throw new Error(detail || `${fallback} (HTTP ${res.status})`);
}

/**
 * 读取后端 GET 型 SSE 帧流（无游标重连语义，如 /sessions/{id}/events 会话事件流）。
 *
 * 与 postSseStream 同款帧解析，但为 GET 订阅：单次连接读到流结束或 abort；
 * 首帧前失败（4xx/5xx）抛出 Error（detail 优先），由调用方决定重连或降级轮询。
 */
export async function readSseFrames(
  url: string,
  onFrame: (frame: SseFrame) => void,
  signal?: AbortSignal
): Promise<void> {
  const res = await fetch(url, { headers: getAuthHeaders(), signal });
  if (!res.ok) {
    await throwHttpError(res, '订阅事件流失败');
  }
  if (!res.body) {
    throw new Error('订阅事件流失败：响应没有事件流');
  }
  await consumeSseFrames(res.body, onFrame);
}

/**
 * 读取后端 POST 型 SSE 流（JSON body + Authorization 头）。
 *
 * 与 readSseEvents 同款帧解析，但面向请求/响应式流（如 /sessions/{id}/chat/stream）：
 * 无游标重连语义，单次订阅到底。首帧前失败（上游 4xx/5xx）不走 SSE，服务端返回
 * JSON 错误体（FastAPI detail），此处读取后以 Error 抛出，由调用方按非流式错误处理。
 */
export async function postSseStream(
  url: string,
  body: unknown,
  onFrame: (frame: SseFrame) => void,
  signal?: AbortSignal
): Promise<void> {
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: JSON.stringify(body),
    signal,
  });
  if (!res.ok) {
    // 首帧前失败：读 JSON 错误体（detail 优先），非 JSON 时回退原文
    await throwHttpError(res, '请求失败');
  }
  if (!res.body) {
    throw new Error('响应没有事件流');
  }
  await consumeSseFrames(res.body, onFrame);
}

/**
 * 游标拉取已落库的任务事件（P11 历史通道）：返回 seq > after 的事件页。
 */
export async function fetchJobEvents(jobId: string, after = 0): Promise<JobEventsPage> {
  const res = await fetch(`/api/v1/jobs/${jobId}/events/history?after=${after}`, {
    headers: getAuthHeaders(),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`拉取任务事件失败 (HTTP ${res.status})${text ? `: ${text}` : ''}`);
  }
  return (await res.json()) as JobEventsPage;
}

/**
 * 事件驱动的任务进度订阅（P11 双通道组合，页面刷新不丢进度）：
 * 1. 先按游标拉取已落库的历史事件重建进度（自动翻页）；
 * 2. 历史中已含 done 终态事件则直接结束；
 * 3. 否则以最新 seq 为游标接 SSE 实时流（断线自动重连续传）。
 *
 * 历史拉取失败（如事件尚未落库完成）时降级为纯 SSE 订阅。
 */
export async function streamJobEvents(
  jobId: string,
  onEvent: (event: SseEvent) => void,
  signal?: AbortSignal,
  options?: SseReadOptions
): Promise<void> {
  let lastSeq = options?.after ?? 0;
  let terminal = false;
  try {
    for (;;) {
      const page = await fetchJobEvents(jobId, lastSeq);
      for (const event of page.events) {
        if (typeof event.seq === 'number' && event.seq > lastSeq) lastSeq = event.seq;
        if (event.type === 'done') terminal = true;
        onEvent(event);
      }
      if (terminal || !page.has_more) break;
    }
  } catch {
    // 历史通道不可用：退化为 SSE（服务端内存日志重放同样不漏不重）
  }
  if (terminal || signal?.aborted) return;
  await readSseEvents(`/api/v1/jobs/${jobId}/events`, onEvent, signal, {
    ...options,
    after: lastSeq,
  });
}
